import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import { format, parseISO } from 'date-fns';
import { getThread, MessageSummary } from '../api/mailbox';
import MessagePage from './MessagePage';
import MessageContextMenu, {
  MessageActionTarget,
  setMessageDragData,
} from '../components/MessageContextMenu';

export default function ThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const id = parseInt(threadId!, 10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredMsg = parseInt(searchParams.get('msg') || '', 10);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(
    Number.isFinite(preferredMsg) ? preferredMsg : null
  );
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [menuMessage, setMenuMessage] = useState<MessageActionTarget | null>(null);

  const { data: thread, isLoading, isError } = useQuery({
    queryKey: ['thread', id],
    queryFn: () => getThread(id),
    enabled: !!id,
  });

  const messages = (thread?.messages || [])
    .slice()
    .sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return da - db;
    });

  useEffect(() => {
    if (!messages.length) return;
    if (selectedMessageId && messages.some((m) => m.id === selectedMessageId)) return;
    if (Number.isFinite(preferredMsg) && messages.some((m) => m.id === preferredMsg)) {
      setSelectedMessageId(preferredMsg);
      return;
    }
    setSelectedMessageId(messages[messages.length - 1].id);
  }, [messages, preferredMsg, selectedMessageId]);

  const activeId = selectedMessageId;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !thread) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load conversation.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <Box
        sx={{
          width: { xs: '100%', md: 340 },
          borderRight: { md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: '40%', md: '100%' },
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="primary.main" sx={{ lineHeight: 1 }}>
              Conversation
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {thread.subject || '(no subject)'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {thread.message_count} message{thread.message_count === 1 ? '' : 's'} in thread
            </Typography>
          </Box>
        </Stack>
        <Divider />
        <List dense disablePadding sx={{ overflow: 'auto', flexGrow: 1 }}>
          {messages.map((msg: MessageSummary, index: number) => (
            <ListItemButton
              key={msg.id}
              selected={msg.id === activeId}
              draggable
              onDragStart={(e) =>
                setMessageDragData(e, {
                  id: msg.id,
                  folder: msg.folder,
                  from_address: msg.from_address,
                  subject: msg.subject,
                })
              }
              onContextMenu={(e) => {
                e.preventDefault();
                setMenuMessage({
                  id: msg.id,
                  folder: msg.folder,
                  from_address: msg.from_address,
                  to_addresses: msg.to_addresses,
                  cc_addresses: msg.cc_addresses,
                  subject: msg.subject,
                });
                setMenuPos({ top: e.clientY, left: e.clientX });
              }}
              onClick={() => setSelectedMessageId(msg.id)}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={msg.is_read ? 400 : 700} noWrap>
                    {index + 1}. {msg.from_address}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap component="span">
                    {msg.date ? format(parseISO(msg.date), 'MMM d, HH:mm') : ''}
                    {' · '}
                    {(msg.subject || '').slice(0, 40)}
                  </Typography>
                }
              />
              {!msg.is_read && <Chip label="new" size="small" color="primary" />}
            </ListItemButton>
          ))}
        </List>
        {activeId && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <IconButton
              size="small"
              onClick={() => navigate(`/compose?reply=${activeId}`)}
              aria-label="Reply"
            >
              <ReplyIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeId ? (
          <MessagePage embeddedMessageId={activeId} hideBack />
        ) : (
          <Paper sx={{ m: 2, p: 3 }}>
            <Typography color="text.secondary">Select a message in the thread.</Typography>
          </Paper>
        )}
      </Box>

      <MessageContextMenu
        anchorPosition={menuPos}
        message={menuMessage}
        onClose={() => {
          setMenuPos(null);
          setMenuMessage(null);
        }}
      />
    </Box>
  );
}
